import type { PrivacyController } from "./controller.js";
import type { ChangeResult, PrivacySnapshot, PrivacyState } from "./types.js";

export interface InlineControlsOptions {
  root: HTMLElement;
  controller: PrivacyController;
  heading?: string;
  onChange?: (result: ChangeResult) => void | Promise<void>;
}

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(name);
  if (className) node.className = className;
  return node;
}

function statusText(snapshot: PrivacySnapshot): string {
  if (snapshot.mode === "essential-only") return "Only essential technology is in use.";
  const optional = snapshot.services.filter((service) => service.category !== "essential");
  const enabled = optional.filter((service) => service.state === "on").length;
  if (snapshot.gpc) return "Optional technology is off because Global Privacy Control is active.";
  if (enabled === 0) return "Optional technology is off.";
  if (enabled === optional.length) return "Your selected optional technology is on.";
  return `${enabled} of ${optional.length} optional services are on.`;
}

export function mountInlinePrivacyControls(options: InlineControlsOptions): () => void {
  const { root, controller } = options;
  root.replaceChildren();
  root.classList.add("miko-privacy-controls");

  const heading = element("h2", "miko-privacy-controls__heading");
  heading.textContent = options.heading ?? "Your privacy choices";
  root.append(heading);

  const initial = controller.snapshot();
  if (initial.mode === "essential-only") {
    const message = element("p", "miko-privacy-controls__status");
    message.textContent = statusText(initial);
    root.append(message);
    return controller.subscribe((snapshot) => {
      message.textContent = statusText(snapshot);
    });
  }

  const explanation = element("p", "miko-privacy-controls__explanation");
  explanation.textContent =
    initial.mode === "uk-statistics-opt-out"
      ? "You can object to optional audience measurement at any time. Your choice is free and does not affect access to the site."
      : "Optional technology stays off unless you choose to allow it. You can change your choice at any time.";
  root.append(explanation);

  const form = element("form", "miko-privacy-controls__form");
  const fieldset = element("fieldset", "miko-privacy-controls__fieldset");
  const legend = element("legend", "miko-privacy-controls__legend");
  legend.textContent = "Optional services";
  fieldset.append(legend);
  const inputs = new Map<string, HTMLInputElement>();

  for (const service of initial.services.filter((item) => item.category !== "essential")) {
    const row = element("div", "miko-privacy-controls__row");
    const input = element("input");
    input.type = "checkbox";
    input.id = `miko-privacy-${service.id}`;
    input.name = service.id;
    input.checked = service.state === "on";
    input.disabled = initial.gpc;
    const label = element("label");
    label.htmlFor = input.id;
    label.textContent = service.displayName;
    row.append(input, label);
    fieldset.append(row);
    inputs.set(service.id, input);
  }

  const button = element("button", "miko-privacy-controls__save");
  button.type = "submit";
  button.textContent = "Save privacy choices";
  const status = element("p", "miko-privacy-controls__status");
  status.role = "status";
  status.setAttribute("aria-live", "polite");
  status.textContent = statusText(initial);
  form.append(fieldset, button, status);
  root.append(form);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    button.disabled = true;
    const updates = [...inputs].map(async ([id, input]) => {
      const desired: PrivacyState = input.checked ? "on" : "off";
      const current = controller.snapshot().services.find((service) => service.id === id);
      return current?.state === desired ? null : controller.setServiceState(id, desired);
    });
    void Promise.all(updates)
      .then(async (results) => {
        const changed = results.filter((result): result is ChangeResult => result !== null);
        const last = changed.at(-1);
        if (last) await options.onChange?.(last);
        const persistence = last?.durable === false
          ? " Browser storage is unavailable, so this choice lasts only for this page."
          : " Saved.";
        const reload = last?.reloadRecommended
          ? " Reload this page to finish switching the previously loaded service off."
          : "";
        status.textContent = `${statusText(controller.snapshot())}${persistence}${reload}`;
      })
      .catch(() => {
        status.textContent = "Your choice could not be saved. Optional technology remains off where prior consent is required.";
      })
      .finally(() => {
        button.disabled = false;
      });
  });

  return controller.subscribe((snapshot) => {
    for (const service of snapshot.services) {
      const input = inputs.get(service.id);
      if (input) {
        input.checked = service.state === "on";
        input.disabled = snapshot.gpc;
      }
    }
    status.textContent = statusText(snapshot);
  });
}
